import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  DEFAULT_LABEL_BARCODE_VARIANT,
  renderBarcodeSvg,
  labelBarcodeWidthMm,
  LABEL_BARCODE_HEIGHT_MM,
  LABEL_BARCODE_TOP_MM,
  LABEL_META_FONT_PT,
  LABEL_META_HEIGHT_MM,
  LABEL_PANEL_INSET_MM,
  LABEL_TOP_PAD_MM,
  labelMetaWidthMm,
} from '../../utils/inventoryLabelBarcode';
import {
  LABEL_INFO_FONT_PT,
  LABEL_INFO_PAD_X_MM,
  LABEL_INFO_PROVIDER_GAP_MM,
  LABEL_FONT_FAMILY,
  LABEL_TALLA_FONT_PT,
  LABEL_PROVIDER_FONT_PT,
  LABEL_TEXT_LETTER_SPACING_MM,
} from '../../utils/inventoryLabelGeometry';
import {
  LABEL_PRINT_WIDTH_MM,
  LABEL_PRINT_HEIGHT_MM,
  LABEL_EMPTY_MM,
  LABEL_FOLD_MM,
  LABEL_INFO_LEFT_MM,
  LABEL_BARCODE_LEFT_MM,
  LABEL_INFO_WIDTH_MM,
  LABEL_BARCODE_WIDTH_MM,
} from '../../utils/printInventoryLabels';

/** Preview: empty 44 mm neck + Plata/Bs panel + barcode panel at the strip end. */
export interface InventoryLabelPreviewRow {
  productCodeUnique: string;
  materialLabel: string;
  salePrice: number;
  weightGrams: number | null;
  diameterSize?: string | null;
  workshopCode?: string | null;
}

/**
 * Same font as the printed label. Typography imposes the theme font, so every text
 * sets the family explicitly instead of inheriting it.
 */
const INFO_TEXT_SX = {
  fontFamily: LABEL_FONT_FAMILY,
  fontWeight: 700,
  fontSize: `${LABEL_INFO_FONT_PT}pt`,
  lineHeight: 1,
} as const;

export function InventoryLabelPreviewMini({ row }: { row: InventoryLabelPreviewRow }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [barWidthMm, setBarWidthMm] = useState(0);
  const workshopCode = (row.workshopCode ?? '').trim();
  const talla = (row.diameterSize ?? '').trim();

  useEffect(() => {
    const svg = svgRef.current;
    // One unit per module, so the SVG width attribute ("90px") is the module count.
    renderBarcodeSvg(svg, row.productCodeUnique ?? '', { width: 1, height: 1 });
    const modules = Number.parseFloat(svg?.getAttribute('width') ?? '');
    svg?.setAttribute('preserveAspectRatio', 'none');
    setBarWidthMm(modules > 0 ? labelBarcodeWidthMm(modules, DEFAULT_LABEL_BARCODE_VARIANT) : 0);
  }, [row.productCodeUnique]);

  const priceStr = `Bs. ${Number(row.salePrice).toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
  const weight =
    row.weightGrams != null && !Number.isNaN(Number(row.weightGrams))
      ? Number(row.weightGrams).toLocaleString('es-BO', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      : '';

  return (
    <Box
      sx={{
        position: 'relative',
        width: `${LABEL_PRINT_WIDTH_MM}mm`,
        height: `${LABEL_PRINT_HEIGHT_MM}mm`,
        overflow: 'hidden',
        bgcolor: '#fff',
        color: '#111827',
        fontFamily: LABEL_FONT_FAMILY,
        fontWeight: 700,
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: `${LABEL_FOLD_MM}mm`,
          top: 0,
          bottom: 0,
          borderLeft: '1px dotted',
          borderColor: 'grey.400',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: `${LABEL_EMPTY_MM}mm`,
          top: 0,
          bottom: 0,
          borderLeft: '1px dashed',
          borderColor: 'grey.600',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: `${LABEL_BARCODE_LEFT_MM}mm`,
          top: 0,
          bottom: 0,
          width: `${LABEL_BARCODE_WIDTH_MM}mm`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: `${LABEL_PANEL_INSET_MM}mm`,
            top: `${LABEL_TOP_PAD_MM}mm`,
            width: `${labelMetaWidthMm(barWidthMm)}mm`,
            lineHeight: 1,
          }}
        >
          <Box
            sx={{
              height: `${LABEL_META_HEIGHT_MM}mm`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '0.5mm',
              fontSize: `${LABEL_META_FONT_PT}pt`,
              letterSpacing: `${LABEL_TEXT_LETTER_SPACING_MM}mm`,
            }}
          >
            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
              {row.productCodeUnique}
            </Box>
            {weight ? (
              <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                {weight}
              </Box>
            ) : null}
          </Box>
          <svg
            ref={svgRef}
            style={{
              display: 'block',
              marginTop: `${LABEL_BARCODE_TOP_MM - LABEL_TOP_PAD_MM - LABEL_META_HEIGHT_MM}mm`,
              width: `${barWidthMm}mm`,
              height: `${LABEL_BARCODE_HEIGHT_MM}mm`,
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          left: `${LABEL_INFO_LEFT_MM}mm`,
          top: 0,
          bottom: 0,
          width: `${LABEL_INFO_WIDTH_MM}mm`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          overflow: 'hidden',
          px: `${LABEL_INFO_PAD_X_MM}mm`,
          boxSizing: 'border-box',
        }}
      >
        <Typography sx={{ ...INFO_TEXT_SX, fontStyle: 'italic' }} noWrap>
          {row.materialLabel}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: '0.5mm',
          }}
        >
          <Typography sx={INFO_TEXT_SX} noWrap>
            {priceStr}
          </Typography>
          {talla !== '' ? (
            <Typography
              component="span"
              sx={{ ...INFO_TEXT_SX, fontSize: `${LABEL_TALLA_FONT_PT}pt`, flex: '0 0 auto' }}
              noWrap
            >
              {talla}
            </Typography>
          ) : null}
        </Box>
        <Typography
          sx={{
            ...INFO_TEXT_SX,
            fontSize: `${LABEL_PROVIDER_FONT_PT}pt`,
            mt: `${LABEL_INFO_PROVIDER_GAP_MM}mm`,
            height: '1em',
          }}
          noWrap
        >
          {workshopCode}
        </Typography>
      </Box>
    </Box>
  );
}
